import clsx from 'clsx';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import SegmentedControl from '@/components/SegmentedControl/SegmentedControl';

import s from './TimeSelect.module.scss';
import { TimeWheel } from './TimeWheel';

/** Module scope, so the array is not a new object on every render. */
const MERIDIEM_OPTIONS = [
  { value: 'AM', label: 'AM' },
  { value: 'PM', label: 'PM' },
] as const;

type Meridiem = 'AM' | 'PM';

/** Breathing room between the field and the list, and against the viewport. */
const GAP = 6;

const COPIES = 5;
const MIDDLE = Math.floor(COPIES / 2);

/** "3:00", "11:45" — the hour without its meridiem, which the segment carries. */
const label12 = (minutes: number): string => {
  const h24 = Math.floor(minutes / 60);
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(minutes % 60).padStart(2, '0')}`;
};

/**
 * Every slot inside one half of the day, as minutes-from-midnight.
 *
 * Half, not the whole day: the meridiem is a separate control, and a list that
 * ran 00:00–23:45 beside an AM/PM switch would be two answers to one question.
 * 48 rows is also a list you can scan, where 96 is one you scroll.
 */
const slots = (step: number, meridiem: Meridiem): number[] => {
  const base = meridiem === 'PM' ? 12 * 60 : 0;
  return Array.from({ length: Math.floor((12 * 60) / step) }, (_, i) => base + i * step);
};

const atMinutes = (base: Date | null, minutes: number): Date => {
  const d = base ? new Date(base) : new Date();
  d.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
  return d;
};

const minutesOf = (d: Date | null): number | null =>
  d ? d.getHours() * 60 + d.getMinutes() : null;

type TimeSelectProps = {
  /** Only hours and minutes are read or written — the date part rides along. */
  value: Date | null;
  onChange: (next: Date) => void;
  /** The row's heading, left of the control. Omit for the bare field. */
  label?: string;
  /** Minutes between options. 15 by default for the list, 5 for the wheel. */
  step?: number;
  className?: string;
  id?: string;
  /** `list` drops a list of times from a field; `wheel` is the iPhone-style
      hour / minute / AM-PM drum, laid out in place, for a card with the room. */
  variant?: 'list' | 'wheel';
};

/**
 * **A time, chosen from a list.** The calendar pattern: the field shows the
 * time, pressing it drops the day in fixed steps, and you pick one.
 *
 * Replaces typing in {@link TimeField} for the date picker. Two digits, two
 * digits and a meridiem is four decisions and three ways to be wrong — an hour
 * out of range, a minute that is not a real one, the meridiem left on the
 * value it happened to have. A list has one decision and no invalid state,
 * and 15 minutes is the granularity nearly every entry actually wants.
 *
 * Typing still works, because a list of 96 rows is slow when you already know
 * the answer: type "215" or "2:15" and the list jumps. What you cannot do is
 * commit something that is not on the list, which is the point.
 *
 * Keyboard throughout, because a select that is mouse-only is half a control:
 * Up/Down walk and wrap, Home/End reach the ends, Enter commits, Escape closes
 * and gives focus back to the field it came from.
 */
const TimeList = ({ value, onChange, label, step = 15, className, id }: TimeSelectProps) => {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [active, setActive] = useState(0);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const typedAt = useRef(0);
  const listRef = useRef<HTMLUListElement | null>(null);

  const current = minutesOf(value);
  const meridiem: Meridiem = current !== null && current >= 12 * 60 ? 'PM' : 'AM';
  const options = useMemo(() => slots(step, meridiem), [step, meridiem]);

  /* Switching the meridiem keeps the hour and the minute and moves the time
     twelve hours — it is the same o'clock in the other half of the day, which is
     what the control is for. It commits immediately rather than waiting for the
     list, because the segment IS the choice at that point. */
  const setMeridiem = (next: Meridiem) => {
    if (next === meridiem) return;
    const mins = current ?? 0;
    onChange(atMinutes(value, next === 'PM' ? mins + 12 * 60 : mins - 12 * 60));
  };

  /* The nearest slot, not an exact match: a time set elsewhere (or seeded from
     "now") is rarely on a 15-minute boundary, and a list with nothing marked is
     a list that has lost your place. */
  const nearest = useMemo(() => {
    if (current === null) return 0;
    let best = 0;
    for (let i = 1; i < options.length; i += 1) {
      if (Math.abs(options[i] - current) < Math.abs(options[best] - current)) best = i;
    }
    return best;
  }, [current, options]);

  /* Opening seeds the cursor here rather than in an effect: the effect would be
     a second render that moves the highlight after the list is already on
     screen, and `nearest` is known before it opens. */
  const openList = () => {
    setActive(nearest);
    setTyped('');
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    /* Land on the current time rather than at midnight — the whole reason this
       reads as a picker and not as a list of every time there is. `auto`, not
       smooth: the list should already be in the right place when it appears. */
    const node = listRef.current?.children[MIDDLE * options.length + nearest] as
      | HTMLElement
      | undefined;
    node?.scrollIntoView({ block: 'center' });
    /* The list takes focus, rather than the button keeping it. Leaving focus on
       the trigger works only as long as the browser put it there — open the list
       any other way and the keys land on nothing, which is a listbox you can see
       and cannot drive. Focus moves back to the field on close. */
    listRef.current?.focus();
  }, [open, nearest, options.length]);

  const recenter = () => {
    const list = listRef.current;
    const first = list?.children[0] as HTMLElement | undefined;
    const nextBlock = list?.children[options.length] as HTMLElement | undefined;
    if (!list || !first || !nextBlock) return;
    const block = nextBlock.offsetTop - first.offsetTop;
    if (list.scrollTop < block) list.scrollTop += block * (MIDDLE - 1);
    else if (list.scrollTop + list.clientHeight > block * (COPIES - 1)) {
      list.scrollTop -= block * (MIDDLE - 1);
    }
  };

  /* The list is portalled to <body> and positioned by hand rather than sitting
     in the flow under the field. Inside the picker it was clipped: the popover
     and the mobile sheet both scroll their own content, so a list longer than
     what was left below the field got cut off at their edge instead of drawing
     over them.

     Placed imperatively, not through state: a popover's coordinates are a
     measurement of the DOM, and routing them through a render only paints the
     list in the wrong place first and moves it after. */
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const trigger = btnRef.current;
      const list = listRef.current;
      if (!trigger || !list) return;
      const rect = trigger.getBoundingClientRect();
      const height = list.offsetHeight;
      const below = window.innerHeight - rect.bottom - GAP;
      /* Upwards only when the list genuinely does not fit below AND there is
         more room above — otherwise a field near the middle of a short window
         would flip for no gain. */
      const up = height > below && rect.top - GAP > below;
      list.style.left = `${Math.max(GAP, Math.min(rect.left, window.innerWidth - list.offsetWidth - GAP))}px`;
      list.style.minWidth = `${rect.width}px`;
      list.style.top = up
        ? `${Math.max(GAP, rect.top - GAP - height)}px`
        : `${rect.bottom + GAP}px`;
    };
    place();
    /* `true` — the field can sit inside a scrolling sheet, and a scroll there
       does not reach window. */
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (
        !listRef.current?.contains(e.target as Node) &&
        !btnRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const commit = (index: number) => {
    onChange(atMinutes(value, options[index]));
    setOpen(false);
    btnRef.current?.focus();
  };

  const move = (next: number) => {
    setActive(next);
    (
      listRef.current?.children[MIDDLE * options.length + next] as HTMLElement | undefined
    )?.scrollIntoView({
      block: 'nearest',
    });
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
        e.preventDefault();
        openList();
      }
      return;
    }
    const last = options.length - 1;
    /* While the list is open it owns its keys. Without this they bubble: the
       picker this sits inside closes on Escape too, so one press shut the list
       AND the whole calendar, and Enter reached the form behind and submitted
       it. A control nested in a picker nested in a modal has to stop what it
       handles. */
    const handled = ['Escape', 'ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter'];
    if (handled.includes(e.key) || /^[0-9:]$/.test(e.key)) e.stopPropagation();
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      btnRef.current?.focus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      move(active === last ? 0 : active + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      move(active === 0 ? last : active - 1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      move(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      move(last);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      commit(active);
    } else if (/^[0-9:]$/.test(e.key)) {
      /* Typeahead. "2", "21", "215" and "2:15" all narrow to the same place.

         The buffer clears after a second's pause, and that is not a nicety:
         without it a second attempt concatenates onto the first, so typing
         "215" twice reads as "5215" and lands on 04:15 — a time nobody asked
         for, arrived at silently. One second is the usual select-typeahead
         window. */
      const now = Date.now();
      const carry = now - typedAt.current < 1000 ? typed : '';
      typedAt.current = now;
      const next = (carry + e.key).slice(-4);
      setTyped(next);
      const digits = next.replace(/:/g, '');
      /* Three digits are H:MM, not HH:M — "215" is a quarter past two, which is
         what anyone typing it means. Four are HH:MM. One or two are the hour. */
      const [hh, mm] =
        digits.length >= 4
          ? [Number(digits.slice(0, 2)), Number(digits.slice(2, 4))]
          : digits.length === 3
            ? [Number(digits.slice(0, 1)), Number(digits.slice(1))]
            : [Number(digits), 0];
      if (!Number.isNaN(hh)) {
        const target = (hh % 24) * 60 + (Number.isNaN(mm) ? 0 : mm);
        let best = 0;
        for (let i = 1; i < options.length; i += 1) {
          if (Math.abs(options[i] - target) < Math.abs(options[best] - target)) best = i;
        }
        move(best);
      }
    }
  };

  const listId = id ? `${id}-listbox` : undefined;

  return (
    <div className={clsx(s.row, className)}>
      {label && <span className={s.label}>{label}</span>}
      <div className={s.wrap}>
        <button
          ref={btnRef}
          id={id}
          type="button"
          className={s.field}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          onClick={() => (open ? setOpen(false) : openList())}
          onKeyDown={onKeyDown}
        >
          {current !== null ? label12(current) : '--:--'}
        </button>
      </div>

      {open &&
        createPortal(
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            /* Portalled out of the picker's DOM, so the picker's own
               click-outside would read a pick as a click elsewhere and shut
               before the choice landed. This marks the node as still belonging
               to whatever floating panel opened it. */
            data-floating-child=""
            className={s.list}
            tabIndex={-1}
            aria-activedescendant={id ? `${id}-opt-${active}` : undefined}
            aria-label={label ?? 'Time'}
            onKeyDown={onKeyDown}
            onScroll={recenter}
          >
            {Array.from({ length: COPIES }, (_, copy) =>
              options.map((m, i) => {
                const isOriginal = copy === MIDDLE;
                return (
                  <li
                    key={`${copy}-${m}`}
                    id={id && isOriginal ? `${id}-opt-${i}` : undefined}
                    role={isOriginal ? 'option' : 'presentation'}
                    aria-selected={isOriginal ? i === nearest : undefined}
                    aria-hidden={isOriginal ? undefined : true}
                    className={clsx(
                      s.option,
                      i === active && s.active,
                      i === nearest && s.selected,
                    )}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => commit(i)}
                  >
                    {label12(m)}
                  </li>
                );
              }),
            )}
          </ul>,
          document.body,
        )}

      {/* The same AM/PM the typed field carries, and the same control: the two
          time inputs in this app should not disagree about what a meridiem looks
          like. SegmentedControl's default 48px matches the field beside it, so
          the pair reads as one inline unit. */}
      <SegmentedControl
        className={s.meridiem}
        aria-label="AM or PM"
        options={MERIDIEM_OPTIONS}
        value={meridiem}
        onChange={setMeridiem}
      />
    </div>
  );
};

export const TimeSelect = ({ variant = 'list', ...props }: TimeSelectProps) =>
  variant === 'wheel' ? (
    <TimeWheel
      value={props.value}
      onChange={props.onChange}
      step={props.step}
      label={props.label}
      className={props.className}
    />
  ) : (
    <TimeList {...props} />
  );
